import { useCallback, useState } from 'react';

import { ErrorFallback, type WidgetErrorProps } from '@/shared/ui/ErrorBoundary';

import { useMemoryUsage } from '@/features/monitoring';
import type { ResourceUsageData } from '@/features/monitoring/api';

import { MetricCard } from '@/widgets/monitoring/MetricCard';
import { PodMetricDetailModal } from '@/widgets/monitoring/PodMetricDetailModal';

import styles from './MemoryMetric.module.css';

type MemoryMetricContentProps = {
  data?: ResourceUsageData;
  isLoading?: boolean;
};

type MemoryMetricViewProps = {
  data?: ResourceUsageData;
  isLoading?: boolean;
};

const MemoryMetricView = ({ data: memoryData, isLoading: memoryLoading = false }: MemoryMetricViewProps) => {
  const [memoryModalOpen, setMemoryModalOpen] = useState(false);

  const handleDetailClick = useCallback(() => {
    setMemoryModalOpen((prev) => !prev);
  }, []);

  return (
    <div className={styles.container}>
      <MetricCard
        title="Memory"
        value={memoryData?.current ?? 0}
        unit={memoryData?.unit ?? 'GB'}
        isLoading={memoryLoading}
        totalCapacity={memoryData?.total}
        percentage={memoryData?.percentage}
        podHistory={
          memoryData?.podHistory.map((pod) => ({
            pod: pod.pod,
            data: pod.data,
            color: pod.color,
          })) ?? []
        }
        onDetailClick={handleDetailClick}
      />

      {memoryData && (
        <PodMetricDetailModal
          open={memoryModalOpen}
          onClose={handleDetailClick}
          title="Memory"
          unit={memoryData.unit}
          podHistory={memoryData.podHistory}
        />
      )}
    </div>
  );
};

const ConnectedContent = () => {
  const { data, isLoading } = useMemoryUsage();

  return <MemoryMetricView data={data} isLoading={isLoading} />;
};

const Content = ({ data, isLoading }: MemoryMetricContentProps) => {
  if (data) {
    return <MemoryMetricView data={data} isLoading={isLoading} />;
  }

  return <ConnectedContent />;
};

const Error = ({ title, description, ...props }: WidgetErrorProps) => {
  return (
    <div className={styles.container}>
      <ErrorFallback title={title} description={description} className={styles.error} {...props} />
    </div>
  );
};

export const MemoryMetric = { Content, Error };
